import React, { useMemo } from 'react';
import {
  Smartphone,
  Shield,
  Key,
  Monitor,
  Clock,
  Hash,
  Bell,
  Phone,
  Package,
  XCircle,
  CheckCircle,
  Globe,
  Eye,
} from 'lucide-react';
import { SectionCard, MetricCard, TabLoading, TabError, TabEmpty, StatusBadge } from './shared';
import { useUserDevices, useUserAuthSessions } from '../../hooks/useUserTabs';
import { cn, formatDateTime, formatRelativeTime } from '../../utils/helpers';
import type { UserDeviceItem, AuthSessionItem, RefreshTokenItem } from '../../types/userTabs';

interface DevicesAuthTabProps {
  userId: number;
}

function truncateToken(token: string | null, len = 16): string {
  if (!token) return '';
  if (token.length <= len) return token;
  return `${token.slice(0, len)}...`;
}

function getDeviceLabel(deviceId: string): string {
  if (deviceId.startsWith('iPhone')) return deviceId;
  if (deviceId === 'arm64') return 'Simulator (arm64)';
  if (deviceId.match(/^[0-9A-F]{8}-/i)) return `UUID ${deviceId.slice(0, 8)}...`;
  return deviceId;
}

function getSessionPlatformLabel(session: AuthSessionItem): string {
  if (session.platform === 'web') return 'Web';
  if (session.deviceLabel) return session.deviceLabel;
  if (session.deviceId === 'xuman') return 'Xuman App';
  return session.deviceId;
}

const DevicesAuthTab: React.FC<DevicesAuthTabProps> = ({ userId }) => {
  const {
    data: devicesData,
    loading: devicesLoading,
    error: devicesError,
    refetch: refetchDevices,
  } = useUserDevices(userId, true);

  const {
    data: authData,
    loading: authLoading,
    error: authError,
    refetch: refetchAuth,
  } = useUserAuthSessions(userId, true);

  const loading = devicesLoading || authLoading;
  const error = devicesError || authError;

  // Normalize devices — handle both { items: [...] } and plain array
  const devices: UserDeviceItem[] = useMemo(() => {
    if (!devicesData) return [];
    if (Array.isArray(devicesData)) return devicesData;
    if (devicesData.items && Array.isArray(devicesData.items)) return devicesData.items;
    return [];
  }, [devicesData]);

  const activeDevices = useMemo(() => devices.filter((d) => d.isActive), [devices]);

  // Sessions split
  const activeSessions = useMemo(
    () => (authData?.sessions || []).filter((s: AuthSessionItem) => !s.revokedAt),
    [authData]
  );
  const revokedSessions = useMemo(
    () => (authData?.sessions || []).filter((s: AuthSessionItem) => s.revokedAt),
    [authData]
  );

  // Only show latest 20 refresh tokens — they can be very numerous
  const recentTokens = useMemo(
    () => (authData?.refreshTokens || []).slice(0, 20),
    [authData]
  );

  if (loading) return <TabLoading />;
  if (error) {
    return (
      <TabError
        message={error}
        onRetry={() => {
          if (devicesError) refetchDevices();
          if (authError) refetchAuth();
        }}
      />
    );
  }
  if (!devices.length && !authData) return <TabEmpty message="No device or session data available" />;

  return (
    <div className="space-y-5">
      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          label="Registered Devices"
          value={devices.length}
          icon={Smartphone}
          color="text-blue-400"
          bgColor="bg-blue-500/10"
          subtitle={`${activeDevices.length} active`}
        />
        <MetricCard
          label="Active Sessions"
          value={authData?.activeSessionCount ?? activeSessions.length}
          icon={Shield}
          color="text-emerald-400"
          bgColor="bg-emerald-500/10"
        />
        <MetricCard
          label="Total Sessions"
          value={authData?.totalSessionCount ?? (authData?.sessions?.length ?? 0)}
          icon={Globe}
          color="text-violet-400"
          bgColor="bg-violet-500/10"
        />
        <MetricCard
          label="Refresh Tokens"
          value={authData?.refreshTokens?.length ?? 0}
          icon={Key}
          color="text-amber-400"
          bgColor="bg-amber-500/10"
        />
      </div>

      {/* Registered Devices */}
      <SectionCard
        title="Registered Devices"
        icon={Smartphone}
        subtitle={`${devices.length} device${devices.length !== 1 ? 's' : ''}`}
      >
        {devices.length > 0 ? (
          <div className="space-y-3">
            {devices.map((device) => (
              <div
                key={device.id}
                className="rounded-lg border border-border/50 bg-muted/5 p-4 space-y-2.5"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className={cn(
                      'p-1.5 rounded-lg',
                      device.isActive ? 'bg-emerald-500/10' : 'bg-gray-500/10'
                    )}>
                      <Monitor className={cn(
                        'h-3.5 w-3.5',
                        device.isActive ? 'text-emerald-400' : 'text-gray-400'
                      )} />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {device.platform} — {getDeviceLabel(device.deviceId)}
                      </p>
                      <p className="text-[10px] text-muted-foreground font-mono">#{device.id}</p>
                    </div>
                  </div>
                  <StatusBadge
                    status={device.isActive ? 'Active' : 'Inactive'}
                    variant={device.isActive ? 'success' : 'default'}
                  />
                </div>

                {/* Tokens & bundle */}
                <div className="flex flex-wrap gap-x-5 gap-y-1.5 pl-9 text-[11px] text-muted-foreground">
                  {device.fcmToken && (
                    <span className="flex items-center gap-1" title={device.fcmToken}>
                      <Bell className="h-3 w-3" />
                      FCM: {truncateToken(device.fcmToken)}
                    </span>
                  )}
                  {device.voipToken && (
                    <span className="flex items-center gap-1" title={device.voipToken}>
                      <Phone className="h-3 w-3" />
                      VoIP: {truncateToken(device.voipToken)}
                    </span>
                  )}
                  {device.bundleId && (
                    <span className="flex items-center gap-1">
                      <Package className="h-3 w-3" />
                      {device.bundleId}
                    </span>
                  )}
                </div>

                {/* Dates */}
                <div className="flex items-center gap-4 pl-9 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1" title={formatDateTime(device.createdAt)}>
                    <Clock className="h-3 w-3" />
                    Registered {formatRelativeTime(device.createdAt)}
                  </span>
                  <span className="flex items-center gap-1" title={formatDateTime(device.updatedAt)}>
                    <Clock className="h-3 w-3" />
                    Updated {formatRelativeTime(device.updatedAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Smartphone className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No registered devices</p>
          </div>
        )}
      </SectionCard>

      {/* Active Sessions */}
      <SectionCard
        title="Active Sessions"
        icon={Shield}
        subtitle={`${activeSessions.length} active session${activeSessions.length !== 1 ? 's' : ''}`}
      >
        {activeSessions.length > 0 ? (
          <div className="space-y-3">
            {activeSessions.map((session: AuthSessionItem) => (
              <div
                key={session.id}
                className="rounded-lg border border-border/50 bg-muted/5 p-4"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-1.5 rounded-lg bg-emerald-500/10 flex-shrink-0">
                      <Monitor className="h-3.5 w-3.5 text-emerald-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {getSessionPlatformLabel(session)}
                      </p>
                      {session.platform && (
                        <p className="text-[10px] text-muted-foreground capitalize">{session.platform}</p>
                      )}
                    </div>
                  </div>
                  <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0 ml-2">
                    #{session.id}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 ml-9 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1" title={formatDateTime(session.lastSeenAt)}>
                    <Eye className="h-3 w-3" />
                    Last seen {formatRelativeTime(session.lastSeenAt)}
                  </span>
                  <span className="flex items-center gap-1" title={formatDateTime(session.createdAt)}>
                    <Clock className="h-3 w-3" />
                    Created {formatRelativeTime(session.createdAt)}
                  </span>
                  <span className="flex items-center gap-1 font-mono" title={session.familyId}>
                    <Hash className="h-3 w-3" />
                    {session.familyId.slice(0, 8)}...
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Shield className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No active sessions</p>
          </div>
        )}
      </SectionCard>

      {/* Revoked Sessions */}
      {revokedSessions.length > 0 && (
        <SectionCard
          title="Revoked Sessions"
          icon={XCircle}
          subtitle={`${revokedSessions.length} revoked session${revokedSessions.length !== 1 ? 's' : ''}`}
        >
          <div className="space-y-3">
            {revokedSessions.map((session: AuthSessionItem) => (
              <div
                key={session.id}
                className="rounded-lg border border-border/50 bg-muted/5 p-4 opacity-60"
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-1.5 rounded-lg bg-red-500/10 flex-shrink-0">
                      <XCircle className="h-3.5 w-3.5 text-red-400" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {getSessionPlatformLabel(session)}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status="Revoked" variant="error" />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 ml-9 text-[11px] text-muted-foreground">
                  <span className="flex items-center gap-1" title={formatDateTime(session.revokedAt!)}>
                    <XCircle className="h-3 w-3" />
                    Revoked {formatRelativeTime(session.revokedAt!)}
                  </span>
                  <span className="flex items-center gap-1" title={formatDateTime(session.createdAt)}>
                    <Clock className="h-3 w-3" />
                    Created {formatRelativeTime(session.createdAt)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {/* Refresh Tokens */}
      <SectionCard
        title="Refresh Tokens"
        icon={Key}
        subtitle={`${authData?.refreshTokens?.length ?? 0} total${recentTokens.length < (authData?.refreshTokens?.length ?? 0) ? ` (showing latest ${recentTokens.length})` : ''}`}
      >
        {recentTokens.length > 0 ? (
          <div className="space-y-2">
            {recentTokens.map((token: RefreshTokenItem) => {
              const isExpired = new Date(token.expiresAt) < new Date();
              const isRevoked = !!token.revokedAt;
              const isUsed = !!token.usedAt;

              return (
                <div
                  key={token.id}
                  className={cn(
                    'rounded-lg border border-border/50 bg-muted/5 px-4 py-3 flex items-center justify-between',
                    (isRevoked || isExpired) && 'opacity-50'
                  )}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <Key className={cn(
                      'h-3.5 w-3.5 flex-shrink-0',
                      isRevoked ? 'text-red-400' : isExpired ? 'text-muted-foreground' : 'text-amber-400'
                    )} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-foreground font-mono">#{token.id}</span>
                        <span className="text-[10px] text-muted-foreground">
                          Session #{token.sessionId}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-muted-foreground mt-0.5">
                        <span title={formatDateTime(token.createdAt)}>
                          Created {formatRelativeTime(token.createdAt)}
                        </span>
                        <span title={formatDateTime(token.expiresAt)}>
                          Expires {formatRelativeTime(token.expiresAt)}
                        </span>
                        {isUsed && (
                          <span title={formatDateTime(token.usedAt!)}>
                            Used {formatRelativeTime(token.usedAt!)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-2">
                    {isRevoked ? (
                      <StatusBadge status="Revoked" variant="error" />
                    ) : isExpired ? (
                      <StatusBadge status="Expired" variant="default" />
                    ) : isUsed ? (
                      <StatusBadge status="Used" variant="info" />
                    ) : (
                      <StatusBadge status="Active" variant="success" />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="py-8 text-center">
            <Key className="h-6 w-6 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-xs text-muted-foreground">No refresh tokens</p>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

export default DevicesAuthTab;
